import React, { useState } from "react";
import { Box, Snackbar, Alert } from "@mui/material";
import {
  ReservationScheduledResponse,
  CourtTypes,
} from "../../types/generalTypes";
import { markReservationAsPaid } from "../../api/ReservationsAPI";
import ModernScheduleHeader from "./ModernScheduleHeader";
import ModernCalendarView from "./ModernCalendarView";

interface ScheduledHoursProps {
  reservations: ReservationScheduledResponse[];
  setReservations: React.Dispatch<
    React.SetStateAction<ReservationScheduledResponse[]>
  >;
  startHour?: number;
  endHour?: number;
  displayedDate: Date;
  companyId?: string | number;
  onReservationUpdate?: () => void;
  selectedCourts?: string[];
  allCourts?: CourtTypes[];
  viewMode: "daily" | "weekly";
  onViewModeChange: (mode: "daily" | "weekly") => void;
}

const ScheduledHours = ({
  reservations,
  setReservations,
  startHour = 7,
  endHour = 24,
  displayedDate,
  companyId,
  onReservationUpdate,
  selectedCourts = [],
  allCourts = [],
  viewMode,
  onViewModeChange,
}: ScheduledHoursProps) => {
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleTogglePaid = async (
    reservation: ReservationScheduledResponse,
  ) => {
    try {
      if (reservation.id) {
        await markReservationAsPaid(reservation.id, !reservation.isPaid);
        setReservations((prev) =>
          prev.map((r) =>
            r.id === reservation.id ? { ...r, isPaid: !reservation.isPaid } : r,
          ),
        );
        setSnackbar({
          open: true,
          message: !reservation.isPaid
            ? "✅ Reserva marcada como paga!"
            : "⚠️ Reserva marcada como não paga!",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "❌ Erro ao atualizar status de pagamento.",
        severity: "error",
      });
    }
  };

  const handleEventDrop = async (info: any) => {
    // Handle drag and drop event updates
    // You can implement API call here to update the reservation time
    console.log("Event dropped:", info);

    setSnackbar({
      open: true,
      message: "🔄 Funcionalidade de arrastar e soltar em desenvolvimento",
      severity: "info",
    });

    // Revert the change for now
    info.revert();
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          overflow: "hidden",
          border: "1px solid #E5E7EB",
        }}
      >
        <ModernScheduleHeader
          displayedDate={displayedDate}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
        />

        <Box sx={{ padding: { xs: "16px", md: "24px" } }}>
          <ModernCalendarView
            reservations={reservations}
            displayedDate={displayedDate}
            selectedCourts={selectedCourts}
            onTogglePaid={handleTogglePaid}
            onEventDrop={handleEventDrop}
            startHour={startHour}
            endHour={endHour}
            viewMode={viewMode}
          />
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ScheduledHours;
